import ApiService from '@services/api.service'
import { Notify } from 'quasar'

export const antenneSecteurHoraires = {
  state: {
    horaires: [],
    horaire: null
  },
  mutations: {
    setHoraires(state, horaires) {
      state.horaires = horaires
    },
    setHoraire(state, horaire) {
      state.horaire = horaire
    },
    addHoraire(state, horaire) {
      if (!state.horaires) {
        state.horaires = []
      }
      state.horaires.push(horaire)
    },
    updateHoraire(state, updatedHoraire) {
      const index = state.horaires.findIndex((h) => h.id === updatedHoraire.id)
      if (index !== -1) {
        state.horaires.splice(index, 1, updatedHoraire)
      }
    },
    removeHoraire(state, horaireId) {
      state.horaires = state.horaires.filter((h) => h.id !== horaireId)
    }
  },
  actions: {
    /**
     * GET :: Récupère la liste des horaires d'un secteur d'antenne
     * @param {Object} context - Le contexte Vuex
     * @param {number} secteurId - L'ID du secteur d'antenne
     */
    async fetchHorairesBySecteur(context, secteurId) {
      try {
        const response = await ApiService.getRequest(
          `/antenne_secteur_horaires?antenneSecteur.id=${secteurId}`
        )
        context.commit('setHoraires', response.data['hydra:member'])
        return response.data['hydra:member']
      } catch (error) {
        console.error(error)
        Notify.create({
          message: "Erreur lors de la récupération des horaires",
          color: 'red'
        })
        throw error
      }
    },

    /**
     * POST :: Crée un nouvel horaire pour un secteur d'antenne
     * @param {Object} context - Le contexte Vuex
     * @param {Object} payload - Les données de l'horaire
     * @param {number} payload.secteurId - L'ID du secteur d'antenne (IRI: /antenne_secteurs/{id})
     * @param {string} payload.day - Le jour de la semaine
     * @param {Object} payload.horaire - Les données des horaires
     */
    async createHoraire(context, { secteurId, day, horaire }) {
      try {
        const payload = {
          antenneSecteur: `/antenne_secteurs/${secteurId}`,
          day,
          morningHourBeginPart1: horaire.morning_hour_begin_part1 || '09:00',
          morningHourEndPart1: horaire.morning_hour_end_part1 || '10:00',
          morningHourBeginPart2: horaire.morning_hour_begin_part2 || '10:00',
          morningHourEndPart2: horaire.morning_hour_end_part2 || '12:00',
          afternoonHourBeginPart1: horaire.afternoon_hour_begin_part1 || '14:00',
          afternoonHourEndPart1: horaire.afternoon_hour_end_part1 || '16:00',
          afternoonHourBeginPart2: horaire.afternoon_hour_begin_part2 || '16:00',
          afternoonHourEndPart2: horaire.afternoon_hour_end_part2 || '18:00'
        }

        const response = await ApiService.postRequest(
          '/antenne_secteur_horaires',
          payload
        )

        if (response.status === 201) {
          context.commit('addHoraire', response.data)
          Notify.create({
            message: 'Horaire créé avec succès',
            color: 'green'
          })
          return response.data
        }
      } catch (error) {
        console.error(error)
        Notify.create({
          message: "Erreur lors de la création de l'horaire",
          color: 'red'
        })
        throw error
      }
    },

    /**
     * PATCH :: Met à jour un horaire existant
     * @param {Object} context - Le contexte Vuex
     * @param {Object} payload - Les données de l'horaire
     * @param {number} payload.id - L'ID de l'horaire à mettre à jour
     * @param {Object} payload.horaire - Les nouvelles données des horaires
     */
    async updateHoraire(context, { id, horaire }) {
      try {
        const payload = {
          morningHourBeginPart1: horaire.morning_hour_begin_part1 || '09:00',
          morningHourEndPart1: horaire.morning_hour_end_part1 || '10:00',
          morningHourBeginPart2: horaire.morning_hour_begin_part2 || '10:00',
          morningHourEndPart2: horaire.morning_hour_end_part2 || '12:00',
          afternoonHourBeginPart1: horaire.afternoon_hour_begin_part1 || '14:00',
          afternoonHourEndPart1: horaire.afternoon_hour_end_part1 || '16:00',
          afternoonHourBeginPart2: horaire.afternoon_hour_begin_part2 || '16:00',
          afternoonHourEndPart2: horaire.afternoon_hour_end_part2 || '18:00'
        }

        const response = await ApiService.patchRequest(
          `/antenne_secteur_horaires/${id}`,
          payload
        )

        if (response.status === 200) {
          context.commit('updateHoraire', response.data)
          Notify.create({
            message: 'Horaire mis à jour avec succès',
            color: 'green'
          })
          return response.data
        }
      } catch (error) {
        console.error(error)
        Notify.create({
          message: "Erreur lors de la mise à jour de l'horaire",
          color: 'red'
        })
        throw error
      }
    },

    /**
     * POST/PATCH :: Sauvegarde plusieurs horaires d'un secteur (création ou mise à jour)
     * @param {Object} context - Le contexte Vuex
     * @param {Object} payload
     * @param {number} payload.secteurId - L'ID du secteur d'antenne
     * @param {Array} payload.horaires - Tableau des horaires à sauvegarder
     */
    async saveHorairesForSecteur(context, { secteurId, horaires }) {
      try {
        const promises = horaires.map((horaire) => {
          if (horaire.id) {
            // Mise à jour d'un horaire existant
            return context.dispatch('updateHoraire', {
              id: horaire.id,
              horaire
            })
          } else {
            // Création d'un nouvel horaire
            return context.dispatch('createHoraire', {
              secteurId,
              day: horaire.day,
              horaire
            })
          }
        })

        const results = await Promise.all(promises)

        Notify.create({
          message: 'Horaires enregistrés avec succès',
          color: 'green'
        })

        return results
      } catch (error) {
        console.error(error)
        Notify.create({
          message: "Erreur lors de l'enregistrement des horaires",
          color: 'red'
        })
        throw error
      }
    },

    /**
     * DELETE :: Supprime un horaire
     * @param {Object} context - Le contexte Vuex
     * @param {number} horaireId - L'ID de l'horaire à supprimer
     */
    async deleteHoraire(context, horaireId) {
      try {
        const response = await ApiService.deleteRequest(
          `/antenne_secteur_horaires/${horaireId}`
        )

        if (response.status === 204) {
          context.commit('removeHoraire', horaireId)
          Notify.create({
            message: 'Horaire supprimé avec succès',
            color: 'green'
          })
          return true
        }
      } catch (error) {
        console.error(error)
        Notify.create({
          message: "Erreur lors de la suppression de l'horaire",
          color: 'red'
        })
        throw error
      }
    }
  },
  getters: {
    getHoraires(state) {
      return state.horaires
    },
    getHoraire(state) {
      return state.horaire
    },
    getHorairesByDay: (state) => (day) => {
      return state.horaires.filter((h) => h.day === day)
    }
  }
}
